/**
 * ActivityContent.js
 *
 * Content section for activity cards.
 * Compact layout: Title → Location + Rating/Price side-by-side → Attribute pills
 * Place type is shown in the CreatorRow badge (top-right), not repeated here.
 */

import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../shared/Icon';
import { CONTEXT_CHIP_ICONS } from '../../../utils/contextChips';
import { formatVisitDate, formatCompactCount } from '../../../utils/dateUtils';

/**
 * Shorten a full address to city-level display.
 * "1 University Drive, Aliso Viejo, CA 92656, USA" → "Aliso Viejo, CA"
 */
const shortenLocation = (location) => {
  if (!location) return '';
  const parts = location.split(',').map(p => p.trim());
  if (parts.length <= 2) return location;
  const city = parts[1];
  const stateRaw = parts[2];
  const state = stateRaw ? stateRaw.replace(/\s*\d{4,}.*$/, '') : '';
  return state ? `${city}, ${state}` : city;
};

/**
 * Format large numbers compactly: 1234 → "1.2K" (en) / "1,2K" (nl).
 * Delegates to the shared Intl-based helper — the old local version was
 * English-only and broke above a million ("1200K" instead of "1.2M").
 */
const formatCount = (n) => formatCompactCount(n);

/**
 * Main ActivityContent Component
 */
const ActivityContent = ({ data }) => {
  const { t } = useTranslation('feedCard');

  /**
   * Format price level to dollar signs
   */
  const formatPrice = (price) => {
    if (!price) return null;
    if (price === 'free' || price === 'Free') return t('activityContent.priceFree');
    if (typeof price === 'number') {
      return '$'.repeat(Math.min(4, Math.max(1, price)));
    }
    return price;
  };

  /**
   * Build attribute pills from available data
   */
  const getAttributePills = (typeData) => {
    const pills = [];

    if (typeData.isHiddenGem) {
      pills.push({ label: t('activityContent.pills.hiddenGem.label'), icon: 'diamond' });
    }
    if (typeData.goodForChildren || typeData.isFamilyFriendly) {
      pills.push({ label: t('activityContent.pills.familyFriendly.label'), icon: 'userGroup' });
    }

    // Add up to 2 user tags/vibes
    const tags = [...(typeData.userTags || []), ...(typeData.userVibes || [])];
    for (const tag of tags) {
      if (pills.length >= 3) break;
      const label = tag.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      if (!pills.some(p => p.label.toLowerCase() === label.toLowerCase())) {
        pills.push({ label });
      }
    }

    return pills.slice(0, 3);
  };

  const { title, location, typeData, badge, contextChip } = data;
  const {
    rating,
    priceLevel,
    venue,
    city,
    matchReason,
    matchType,
    googleRating,
    googleRatingCount,
    primaryType,
    visitDate,
    visitDatePrecision,
    isMemory
  } = typeData || {};

  // Backdated visit ("I've been there before" / late confirmation): label the
  // card with when the visit actually happened, so its recent feed placement
  // reads as "shared recently", not "happened recently".
  const memoryDate = isMemory && visitDate
    ? formatVisitDate(visitDate, visitDatePrecision)
    : null;

  // Show place type (e.g. "Museum") when we have a specific type beyond generic "Activity"
  const showPlaceType = !!primaryType && badge?.label && badge.label !== 'Activity';

  // Smart location: prefer venue+city for events, shorten addresses for places
  const displayLocation = (venue && city) ? `${venue}, ${city}`
    : city || shortenLocation(location);

  // Use Google rating for the card (community signal), fall back to user's personal rating
  const displayRating = googleRating || rating;
  const displayRatingCount = googleRatingCount;
  const isGoogleRating = !!googleRating;

  const formattedPrice = formatPrice(priceLevel);
  const pills = getAttributePills(typeData || {});

  return (
    <div className="flex-grow">
      {/* Title */}
      <h3 className="font-semibold text-content-primary mb-1 line-clamp-2">
        {title}
      </h3>

      {/* Place type label (e.g., "Museum", "Restaurant") */}
      {showPlaceType && (
        <div className="flex items-center gap-1 mb-1">
          {badge.icon && <Icon name={badge.icon} className="w-3 h-3 text-content-muted" />}
          <span className="text-xs text-content-muted">{badge.label}</span>
        </div>
      )}

      {/* Location + Rating/Price — side by side */}
      <div className="flex items-center justify-between mb-1">
        {displayLocation && (
          <p className="text-xs text-content-secondary line-clamp-1 flex-1 mr-2">
            {displayLocation}
          </p>
        )}
        {(displayRating || formattedPrice) && (
          <div className="flex items-center gap-2 flex-shrink-0 text-sm">
            {displayRating && (
              <div className="flex items-center gap-0.5">
                <Icon name="star" className="w-3.5 h-3.5 text-accent" />
                <span className="font-medium text-content-muted">{Number(displayRating).toFixed(1)}</span>
                {isGoogleRating && !!displayRatingCount && (
                  <span className="text-xs text-content-muted">({formatCount(displayRatingCount)})</span>
                )}
              </div>
            )}
            {formattedPrice && (
              <span className="text-xs text-content-secondary">{formattedPrice}</span>
            )}
          </div>
        )}
      </div>

      {/* Artist match indicator */}
      {matchReason && (
        <p className="text-xs text-primary font-medium mb-1 line-clamp-1">
          {matchType === 'direct' ? <Icon name="microphone" className="w-3 h-3 inline" /> : <Icon name="link" className="w-3 h-3 inline" />}{' '}{matchReason}
        </p>
      )}

      {/* Attribute pills — memory chip first (temporal identity: this card is
          a throwback, everything else describes the place), then context chip
          (primary-toned: it's about today, not a static attribute; same copy
          as the detail page) */}
      {(memoryDate || contextChip || pills.length > 0) && (
        <div className="flex flex-wrap gap-1 mt-1">
          {memoryDate && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-surface-secondary text-content-secondary">
              <Icon name="clock" className="w-2.5 h-2.5" />
              {t('activityContent.memoryChip', { date: memoryDate })}
            </span>
          )}
          {contextChip && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-primary-lighter text-primary-dark">
              <Icon name={CONTEXT_CHIP_ICONS[contextChip.key] || 'sparkles'} className="w-2.5 h-2.5" />
              {contextChip.label}
            </span>
          )}
          {pills.map((pill) => (
            <span
              key={pill.label}
              className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-surface-secondary text-content-secondary"
            >
              {pill.icon && <Icon name={pill.icon} className="w-2.5 h-2.5" />}
              {pill.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(ActivityContent);
